import React, { useEffect, useState } from 'react';

import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

import { Icon } from '@gerdau/hefesto';

import {
  AlertOrderType,
  AlertWrapper,
  Container,
  ItemAlertWrapper,
  ItemText,
  OrderDetailsValuesContainer,
  OrderItemWrapper
} from './styles';
import OrderAlert from './OrderAlert';
import { useCheckoutMvp } from '../hooks/useCheckoutMvp';
import { currencyFormatter } from '../utils/formatterHelper';
import BottomActionBar from '../BottomActionBar';
import { formatTotalWeight } from '../utils/unitsOfMeasurement';
import { useHistory } from 'react-router-dom';
import { ERoutes } from '../../../../../routes';
import { EPurchaseRoutes } from '../../../purchase.routes';
import { Box } from '../styles';
import { useDispatch } from 'react-redux';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { IOrderSimulateState } from '../../../../../store/ducks/order-simulate-checkout-mvp/types';
import useOnRouteChange from '../../../../../hooks/useOnRouteChange';
import { CHECKOUT_ORDERS_INITIAL_STATE } from '../../../../../store/ducks/order-simulate-checkout-mvp';
import { purchaseOrigin } from '../../../../../services/MVP/utils';
import useGetSenderAndReceiverDetails from '../hooks/useGetSenderAndReceiverDetails';

interface OrderDetailsProps {
  title: string;
  value: string;
  tooltipText?: string;
  placement?: 'bottomLeft' | 'topLeft';
}

interface OrderConfirmedProps {
  emitterId: string;
  receiverId: string;
}

const {
  deleteCartId,
  getCheckoutFromLocalStorage,
  updateCheckoutLocalStorage
} = useCheckoutMvp();

const OrderConfirmedMvp: React.FC<OrderConfirmedProps> = ({
  emitterId,
  receiverId
}) => {
  const { emitterSelected, receiverSelected } =
    useGetSenderAndReceiverDetails();

  const dispatch = useDispatch();
  const [details, setDetails] = useState<OrderDetailsProps[]>([]);
  const history = useHistory();
  const [orderInfoContent, setOrderInfoContent] = useState('');
  const [orderTableContent, setOrderTableContent] = useState('');
  const [customerPurchaseId, setCustomerPurchaseId] = useState('');
  const [checkoutState, setCheckoutState] = useState<IOrderSimulateState>();
  const tipo_compra = purchaseOrigin();

  // limpar localStorage(checkout) sempre que usuário navegar para uma rota de outro conjunto.
  useOnRouteChange(() =>
    updateCheckoutLocalStorage(CHECKOUT_ORDERS_INITIAL_STATE)
  );

  useEffect(() => {
    const checkoutData = getCheckoutFromLocalStorage();
    setCheckoutState(checkoutData);
    setOrderInfoContent(checkoutData.pdf.text ?? '');
    setOrderTableContent(checkoutData.pdf.table ?? '');
    const purchaseId =
      checkoutData.data?.orders[0].order[0].extension_attributes.sap_fields
        .o_header_sap.purch_no_c ?? '';
    setCustomerPurchaseId(purchaseId);

    const setQtyOfItems = () => {
      if (checkoutData.data?.orders) {
        let quantity = 0;
        checkoutData.data.orders.forEach(
          (order) => (quantity += order.order.length)
        );
        return quantity;
      }
      return 0;
    };
    const quantityOfItems = setQtyOfItems();
    const currency = 'BRL';
    const detailsInfo = [
      {
        title: 'Identificação',
        value: purchaseId
      },
      {
        title: 'Preço total',
        value: currencyFormatter({
          amount: Number(
            checkoutData.data?.o_price_sap_totals.price_with_discount_total ??
              '0'
          ),
          currency
        })
      },
      {
        title: 'Quantidade de itens',
        value: String(quantityOfItems)
      },
      {
        title: 'Peso total',
        value: formatTotalWeight(checkoutData.params!.items)
      }
    ];

    setDetails(detailsInfo);
    deleteCartId();
  }, []);

  const handleClickTagging = (eventName: string, screenName: string) => {
    dispatch(
      sendTagging({
        event: eventName,
        screenName: screenName,
        tipo_compra: tipo_compra,
        bp_emissor: emitterId,
        bp_recebedor: receiverId
      })
    );
  };

  const goToPage = (pageUrl: string) => {
    history.push(pageUrl);
  };

  const handleDownload = () => {
    handleClickTagging('baixar_pdf', 'finalizar_pedido');
    const htmlContent = (orderInfoContent ?? '') + orderTableContent;
    const element = document.createElement('div');
    element.innerHTML = htmlContent;
    document.body.appendChild(element);

    html2canvas(element, { useCORS: true, allowTaint: true, scale: 2 }).then(
      (canvas) => {
        const currentDate = new Date();
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const imgWidth = 210;
        const pageHeight = 295;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        // Primeira página
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // páginas adicionais (se necessário)
        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        const pdfFileName = `PEDIDO_${customerPurchaseId}_${currentDate.getFullYear()}_${
          currentDate.getMonth() + 1
        }_${currentDate.getDate()}`;
        pdf.save(pdfFileName);

        document.body.removeChild(element);
      }
    );
  };

  function getMeasurementUnit(value: string): string {
    const spacevalue = value.indexOf(' ');
    return value.slice(spacevalue, value.length).trim();
  }

  const handleTaggingProducts = () => {
    if (!checkoutState) return;

    dispatch(
      sendTagging({
        event: 'purchase',
        ecommerce: {
          currency: 'BRL',
          value:
            checkoutState?.data?.o_price_sap_totals.price_with_discount_total ??
            '0',
          screenName: 'revisar_pedido',
          tipo_compra: tipo_compra,
          bp_emissor: emitterSelected?.customerId ?? emitterId,
          bp_recebedor: receiverSelected?.customerId ?? receiverId,
          nome_emissor: emitterSelected?.name ?? '',
          nome_recebedor: receiverSelected?.name ?? '',
          items: checkoutState?.ordersTable.flatMap((order) =>
            order.orderItems.map((product) => ({
              screenName: 'revisar_pedido',
              tipo_compra: tipo_compra,
              item_id: product.productId,
              item_name: product.itemName,
              item_category:
                checkoutState.params?.items.find(
                  (item) => item.sku === product.productId
                )?.item_category ?? 'item_category',
              price: product.itemPrice,
              unidade_de_medida: getMeasurementUnit(product.itemQuantity),
              quantity: product.itemQuantity,
              data_desejada: product.itemDesiredDate,
              pagamento: product.itemPayment,
              beneficio_fiscal: product.itemTaxBenefit,
              destinacao: product.itemDestiny,
              num_ordem_venda: product.itemPositionNumber,
              preco_sem_impostos: product.itemPrice,
              estimativa_entrega: product.itemDeliveryEstimate.length
                ? product.itemDeliveryEstimate
                : 'Sem estimativa'
            }))
          ),
          transaction_id: checkoutState.params?.cartNumber
        }
      })
    );
  };

  useEffect(() => {
    if (checkoutState) {
      handleTaggingProducts();
    }
  }, [checkoutState]);

  const successTitle = 'Seu pedido foi enviado.';
  const successMessage =
    'Em até 30 minutos estará disponível em “Meus pedidos” e você receberá o e-mail de confirmação.';
  return (
    <Box>
      <Container>
        <AlertWrapper>
          <ItemAlertWrapper>
            <OrderAlert
              title={successTitle}
              message={successMessage}
              type={AlertOrderType.success}
            >
              <Icon option="CheckCircle2" variant="success" size="sm" />
            </OrderAlert>
          </ItemAlertWrapper>

          <ItemAlertWrapper>
            <OrderDetailsValuesContainer>
              {details.map((detail) => (
                <OrderItemWrapper>
                  <ItemText bold>{detail.title}</ItemText>
                  <ItemText>{detail.value}</ItemText>
                </OrderItemWrapper>
              ))}
            </OrderDetailsValuesContainer>
          </ItemAlertWrapper>
        </AlertWrapper>
        <BottomActionBar
          item={{
            value: 'O preço e a estimativa de entrega podem sofrer oscilações.'
          }}
          primaryBtn={{
            title: 'Baixar PDF do pedido',
            primaryEvent: handleDownload
          }}
          secondaryBtn={{
            title: 'Ir para Meus pedidos',
            secondaryEvent: () => {
              handleClickTagging('meus_pedidos', 'finalizar_pedido');
              goToPage(
                `${ERoutes.ORDERS}/${EPurchaseRoutes.STEEL_TYPE_CHOOSE}`
              );
            }
          }}
          littleContent={true}
        />
      </Container>
    </Box>
  );
};

export default OrderConfirmedMvp;
